import { devLog } from '../utils/devLog';
import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging';
import { auth } from './firebaseAuth';
import { toast } from 'react-toastify';
import { emitAppNotification } from './appNotifications';
import { backendApiUrl } from '../config/backendApiUrl';

const TOKEN_KEY = 'fcm_token';
const REG_SENT_KEY = 'fcm_reg_sent';
const REG_UID_KEY = 'fcm_reg_uid';
const REG_AT_KEY = 'fcm_reg_at';
const COOLDOWN_KEY = 'fcm_reg_cooldown_until';
const CLIENT_KEY = 'fcm_client_id';
const CLIENT_SENT_KEY = 'fcm_client_sent';
let fgBound = false;
let regInFlight = false;

// Backend sends severity as normal/high/emergency; client expects info/warning/critical
const SEVERITY_MAP = { normal: 'info', high: 'warning', emergency: 'critical' };
function normalizeSeverity(raw) {
  if (!raw) return 'info';
  const lower = String(raw).toLowerCase();
  return SEVERITY_MAP[lower] || lower; // pass through if already client-style
}

function hasResponseOptions(rawResponses) {
  if (!rawResponses) return false;
  if (Array.isArray(rawResponses)) return rawResponses.length > 0;
  if (typeof rawResponses === 'string') {
    try {
      const parsed = JSON.parse(rawResponses);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch (_) {
      return false;
    }
  }
  return false;
}

function browserClientId() {
  try {
    const existing = localStorage.getItem(CLIENT_KEY);
    if (existing) return existing;
    const created = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sb-${Date.now()}`;
    localStorage.setItem(CLIENT_KEY, created);
    return created;
  } catch (_) {
    return '';
  }
}

async function getSW() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  await navigator.serviceWorker.ready;
  return reg;
}

export function startElectronIncomingEventsNotifications() {
  // Polling disabled — push delivery uses FCM (web) and Electron IPC only.
  return () => {};
}

export async function registerPushToken() {
  try {
    const supported = await isSupported();
    if (!supported) {
      try { console.warn('[FCM] registerPushToken: messaging not supported'); } catch {}
      return null;
    }
    if (regInFlight) return null;
    const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
    try {
      devLog(
        '[FCM] registerPushToken:start ' +
          JSON.stringify({
            isElectron,
            permission: typeof Notification !== 'undefined' ? Notification.permission : 'no-notification',
          })
      );
    } catch {}

    if (isElectron) {
      try {
        const current = typeof Notification !== 'undefined' ? Notification.permission : null;
        if (current !== 'granted') {
          try {
            console.warn(
              '[FCM] registerPushToken: skipping in Electron due to permission ' +
                JSON.stringify({ current })
            );
          } catch {}
          return null;
        }
      } catch (_) {
        return null;
      }
    }

    const now = Date.now();
    const cooldownUntil = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
    if (cooldownUntil && now < cooldownUntil) return null;

    try {
      const permission = await Notification.requestPermission();
      try {
        devLog(
          '[FCM] registerPushToken:permission ' +
            JSON.stringify({ isElectron, permission, current: Notification.permission })
        );
      } catch {}
      if (permission !== 'granted') return null;
    } catch (e) {
      try { console.warn('[FCM] registerPushToken:requestPermission failed', e?.message || 'request failed'); } catch {}
      return null;
    }

    const reg = await getSW();
    try {
      devLog(
        '[FCM] registerPushToken:serviceWorker ' +
          JSON.stringify({ present: !!reg, scope: reg && reg.scope ? reg.scope : null })
      );
    } catch {}
    const messaging = getMessaging();
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    try { devLog('[FCM] registerPushToken:vapid ' + JSON.stringify({ present: Boolean(vapidKey) })); } catch {}
    if (!vapidKey) return null;
    let token = null;
    try {
      token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: reg });
    } catch (e) {
      try {
        console.warn(
          '[FCM] registerPushToken:getToken failed ' +
            JSON.stringify({
              name: e && e.name ? e.name : null,
              code: e && e.code ? e.code : null,
              message: e && e.message ? e.message : 'request failed',
            })
        );
      } catch {}
      return null;
    }
    if (!token) return null;
    try { devLog('[FCM] registerPushToken:token acquired ' + JSON.stringify({ len: token.length })); } catch {}
    localStorage.setItem(TOKEN_KEY, token);
    const user = auth.currentUser;
    if (!user) return token;

    // Dedupe: if we already sent this exact token for this user recently, skip
    const clientId = browserClientId();
    const lastSentToken = localStorage.getItem(REG_SENT_KEY) || '';
    const lastSentUid = localStorage.getItem(REG_UID_KEY) || '';
    const lastSentAt = Number(localStorage.getItem(REG_AT_KEY) || 0);
    const clientSent = localStorage.getItem(CLIENT_SENT_KEY) || '';
    const sameUser = lastSentUid && user.uid && lastSentUid === user.uid;
    const sameToken = lastSentToken && lastSentToken === token;
    const twelveHoursMs = 12 * 60 * 60 * 1000;
    if (sameUser && sameToken && clientId && clientSent === clientId && lastSentAt && now - lastSentAt < twelveHoursMs) {
      return token;
    }

    regInFlight = true;
    const idToken = await user.getIdToken();
    const resp = await fetch(backendApiUrl('/api/v2/notifications/token'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ provider: 'FCM', token, clientId })
    });
    try { devLog('[FCM] registerPushToken:backend response ' + JSON.stringify({ status: resp.status })); } catch {}
    if (resp.status === 429) {
      // Back off for 10 minutes
      localStorage.setItem(COOLDOWN_KEY, String(now + 10 * 60 * 1000));
      return token;
    }
    if (!resp.ok) return token;
    localStorage.setItem(REG_SENT_KEY, token);
    localStorage.setItem(REG_UID_KEY, user.uid);
    localStorage.setItem(REG_AT_KEY, String(now));
    if (clientId) localStorage.setItem(CLIENT_SENT_KEY, clientId);
    return token;
  } catch (e) {
    return null;
  } finally {
    regInFlight = false;
  }
}

export async function unregisterPushToken() {
  try {
    if (!(await isSupported())) return;
    const token = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    const user = auth.currentUser;
    if (user && token) {
      const idToken = await user.getIdToken();
      await fetch(backendApiUrl('/api/v2/notifications/token'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ provider: 'FCM', token })
      });
    }
    const reg = await getSW();
    const messaging = getMessaging();
    try { await deleteToken(messaging); } catch {}
    try { if (reg) await reg.unregister(); } catch {}
  } catch {}
}

export async function bindForegroundToasts() {
  if (fgBound) return;
  try {
    if (!(await isSupported())) return;
    const messaging = getMessaging();
    onMessage(messaging, async (payload) => {
      const n = payload?.notification || {};
      const d = payload?.data || {};
      const rawTitle = (n.title || d.title || '').trim();
      const rawBody = (n.body || d.body || '').trim();
      const hasAction = Boolean((d.actionId || '').trim());
      const hasResponses = hasResponseOptions(d.responses);

      if (!rawTitle && !rawBody && !hasAction && !hasResponses) {
        try {
          devLog('[FCM] skipping non-actionable empty foreground payload');
        } catch (_) {}
        return;
      }

      const t = rawTitle || 'Notification';
      const b = rawBody;

      try {
        devLog('[FCM] foreground message received ' + JSON.stringify({ title: t, hasBody: !!b, dataKeys: Object.keys(d), actionId: d.actionId || 'MISSING', responses: d.responses || 'NONE' }));
      } catch (_) {}

      const event = {
        id: d.eventId || d.messageId || ('fcm-' + Date.now()),
        severity: normalizeSeverity(d.severity),
        visibility: d.visibility || 'in_app_only',
        source: d.source || 'push',
        type: d.type || 'notification',
        context: {
          route: 'fcm_push',
          status: 'delivered',
          meta: { title: rawTitle || null, message: rawBody || null, actionId: d.actionId || null },
        },
        actionId: d.actionId || null,
        responses: d.responses || null,
        timestamp: new Date().toISOString(),
      };

      try {
        await emitAppNotification(event, { skipOSNotification: false });
      } catch (e) {
        // Fallback: show toast directly if emitAppNotification fails
        try { console.warn('[FCM] emitAppNotification failed, falling back to toast:', e?.message || 'request failed'); } catch (_) {}
        toast(t + (b ? `: ${b}` : ''));
      }
    });
    fgBound = true;
  } catch {}
}
